import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Box, Button, Card, CardContent, CardHeader, Divider, Grid, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import React, { useState } from 'react';
import * as yup from 'yup';
import { reqString } from '../../components/forms/formValidations';
import { hasValue } from '../../components/inputs/inputHelpers';
import XTextInput from '../../components/inputs/XTextInput';
import { put } from '../../utils/ajax';
import EditDialog from '../../components/EditDialog';
import { LoadingButton } from '@mui/lab';
import Toast from '../../utils/Toast';
import { titleCase, toTitleCase } from '../../utils/stringHelpers';




interface FormProps {
  isActive: boolean
  schema?: any
  onSubmit: (values: any, actions: FormikHelpers<any>) => any
  onCancel?: () => any
}

const ActivationForm = ({ isActive, onSubmit, onCancel }: FormProps) => {
  const schema = yup.object().shape({ remarks: reqString });

  return (
        <Formik
            initialValues={{}}
            onSubmit={onSubmit}
            validationSchema={schema}
            validateOnBlur
            enableReinitialize={false}
        >
            {({ submitForm, isSubmitting, errors }) => (
                <Form>
                    <Grid
                        container
                        spacing={0}
                    >
                        {
                            hasValue(errors)
                            && (
                                <Grid item
                                      xs={12}>
                                    <Box
                                        display="flex"
                                        pb={1}
                                        justifyContent="center"
                                    >
                                        <Alert severity="error">The reason is required</Alert>
                                    </Box>
                                </Grid>
                            )
                        }
                        <Grid item
                              xs={12}
                        >
                            <Grid item
                                  xs={12}
                            >
                                <XTextInput
                                    name="remarks"
                                    label="Reason"
                                    variant='outlined'
                                    required
                                />
                            </Grid>
                        </Grid>
                        <Grid item
                              xs={12}
                        >
                            <Box py={2}>
                                <Grid
                                    container
                                    spacing={1}
                                    justifyContent="flex-end"
                                >
                                    <Grid item>
                                        <Button
                                            variant="text"
                                            color="primary"
                                            onClick={onCancel}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <LoadingButton
                                            variant="contained"
                                            color={isActive ? 'error' : 'primary'}
                                            onClick={submitForm}
                                            disabled={isSubmitting}
                                            loading={isSubmitting}
                                        >
                                            {isActive ? 'Deactivate' : 'Activate'}
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
  );
};

interface IProps {
  id: string,
  isActive: boolean,
  isLoading: boolean,
  url: string,
  entity: string,
  warning: React.ReactNode
  onDone: () => any
}

const ActivationManagement = ({ id, isActive, isLoading,  url, entity, warning, onDone }: IProps) => {
  const [dialog, setDialog] = useState(false);

  function openDialog() {
    setDialog(true);
  }

  function closeDialog() {
    setDialog(false);
  }

  const handleSubmit = (values: any, actions: FormikHelpers<any>) => {
    const request = {
      id: id,
      decision: !isActive,
      remarks: values.remarks
    };

    put(url, request,
      resp => {
        console.log(resp);
        onDone();
        Toast.success(`${toTitleCase(entity)} ${isActive ? 'deactivated' : 'activated'} successfully.`);
        closeDialog();
      },
      (err) => {
        console.log(err);
        Toast.error(`An error occurred while ${isActive ? 'deactivating' : 'activating'} ${entity}.`);
      },
      () => {
        actions.setSubmitting(false);
      });
  };

  return (
        <Card>
            <CardHeader title="Danger Zone"/>
            <Divider/>
            <CardContent>
                {
                    isActive ?
                        <>
                            <Box
                                sx={{ mb: 2, mt: 1 }}
                            >
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                >
                                    Be aware that you are about to deactivate this {entity}
                                </Typography>
                            </Box>
                            <Button
                                startIcon={<BlockIcon fontSize="small"/>}
                                sx={{
                                  backgroundColor: 'error.main',
                                  color: 'error.contrastText',
                                  '&:hover': { backgroundColor: 'error.dark' }
                                }}
                                variant="contained"
                                disabled={isLoading}
                                onClick={openDialog}
                            >
                                Deactivate {titleCase(entity)}
                            </Button>
                        </>
                      :
                        <>
                            <Box
                                sx={{ mb: 2, mt: 1 }}
                            >
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                >
                                    Be aware that you are about to activate this {entity}.
                                </Typography>
                            </Box>
                            <Button
                                startIcon={<CheckCircleIcon fontSize="small"/>}
                                sx={{
                                  backgroundColor: 'success.main',
                                  color: 'success.contrastText',
                                  '&:hover': { backgroundColor: 'success.dark' }
                                }}
                                variant="contained"
                                disabled={isLoading}
                                onClick={openDialog}
                            >
                                Activate {titleCase(entity)}
                            </Button>
                        </>
                }
            </CardContent>
            <EditDialog
                title={isActive ? `Deactivate ${titleCase(entity)}` : `Activate ${titleCase(entity)}`}
                open={dialog}
                onClose={closeDialog}
            >
                <Box sx={{ mb: 3 }}>
                    <Alert severity='warning'>{warning}</Alert>
                </Box>

                <ActivationForm
                    isActive={isActive}
                    onSubmit={handleSubmit}
                    onCancel={closeDialog} />

            </EditDialog>
        </Card>
  );

};

export default ActivationManagement;
